<template>
  <el-dialog
    :model-value="modelValue"
    title="长篇小说阅读器"
    width="900px"
    top="5vh"
    :close-on-click-modal="false"
    @update:model-value="v => emit('update:modelValue', v)"
    @open="onOpen"
  >
    <div v-loading="loading" class="reader-body">
      <el-alert
        v-if="error && !loading"
        type="warning"
        :title="error"
        :closable="false"
      />

      <template v-else-if="!loading && segments.length > 0">
        <!-- 顶部：进度与书签 -->
        <div class="reader-toolbar">
          <span class="seg-count">共 {{ segments.length }} 段</span>
          <span class="selected-count">已选 {{ selected.length }} 段（上限 20 段）</span>
          <div class="toolbar-actions">
            <el-button
              v-if="bookmark"
              size="small"
              type="primary"
              plain
              @click="goBookmark"
            >
              <el-icon><Star /></el-icon>
              回到书签（{{ bookmarkLabel }}）
            </el-button>
            <el-button
              size="small"
              @click="selected = []"
              :disabled="selected.length === 0"
            >
              清空选择
            </el-button>
          </div>
        </div>

        <!-- 双栏：左目录 + 右预览 -->
        <div class="reader-main">
          <div class="toc-panel" ref="tocPanel">
            <div
              v-for="seg in segments"
              :key="seg.index"
              class="toc-item"
              :class="{
                'is-active': previewIndex === seg.index,
                'is-bookmark': bookmark && bookmark.segment_index === seg.index
              }"
            >
              <el-checkbox
                :model-value="selected.includes(seg.index)"
                :disabled="!selected.includes(seg.index) && selected.length >= 20"
                @change="toggleSelect(seg.index)"
              />
              <span class="toc-title" @click="previewSegment(seg.index)" :title="seg.title">
                {{ seg.title }}
              </span>
              <span class="toc-size">{{ formatBytes(seg.bytes) }}</span>
            </div>
          </div>

          <div class="preview-panel" v-loading="previewLoading">
            <div v-if="previewText" class="preview-text">{{ previewText }}</div>
            <el-empty v-else description="点击左侧章节标题预览内容" :image-size="80" />
          </div>
        </div>
      </template>
    </div>

    <template #footer>
      <el-button @click="emit('update:modelValue', false)">关闭</el-button>
      <el-button
        type="primary"
        :disabled="selected.length === 0"
        :loading="exporting"
        @click="confirmImport"
      >
        导入所选{{ selected.length ? `（${selected.length} 段）` : '' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { getNovelToc, previewNovelSegment, exportNovelSegments } from '../../api/project'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  projectId: {
    type: Number,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'select'])

const loading = ref(false)
const error = ref('')
const segments = ref([])
const bookmark = ref(null)
const selected = ref([]) // 跨页保持：全量索引渲染，无分页丢失问题

const previewIndex = ref(-1)
const previewText = ref('')
const previewLoading = ref(false)
const exporting = ref(false)

const tocPanel = ref(null)

const bookmarkLabel = computed(() => {
  if (!bookmark.value) return ''
  const seg = segments.value.find(s => s.index === bookmark.value.segment_index)
  return seg ? seg.title : `第 ${bookmark.value.segment_index + 1} 段`
})

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes}B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`
}

const onOpen = async () => {
  loading.value = true
  error.value = ''
  segments.value = []
  bookmark.value = null
  selected.value = []
  previewIndex.value = -1
  previewText.value = ''

  try {
    const res = await getNovelToc(props.projectId)
    segments.value = res?.data?.segments || []
    bookmark.value = res?.data?.bookmark || null

    // 打开后自动定位：优先回到书签，否则预览第一段
    const start = bookmark.value?.segment_index ?? 0
    if (segments.value.length > 0) {
      await previewSegment(start)
    }
  } catch (e) {
    console.error('[NovelReader] load toc failed:', e)
    error.value = e.message || '加载分段目录失败'
  } finally {
    loading.value = false
  }
}

// 预览单段（后端同步更新浏览书签）
const previewSegment = async (index) => {
  if (index < 0 || index >= segments.value.length) return
  previewIndex.value = index
  previewLoading.value = true
  try {
    const res = await previewNovelSegment(props.projectId, index)
    previewText.value = res?.data?.content || ''
  } catch (e) {
    console.error('[NovelReader] preview failed:', e)
    ElMessage.error(e.message || '读取段落失败')
    previewText.value = ''
  } finally {
    previewLoading.value = false
  }
}

const toggleSelect = (index) => {
  const pos = selected.value.indexOf(index)
  if (pos >= 0) {
    selected.value.splice(pos, 1)
  } else if (selected.value.length < 20) {
    selected.value.push(index)
  }
}

// 滚动目录到书签位置并预览
const goBookmark = () => {
  if (!bookmark.value) return
  const index = bookmark.value.segment_index
  previewSegment(index)
  const el = tocPanel.value?.querySelector(`.toc-item:nth-child(${index + 1})`)
  el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
}

// 导入所选：后端按时序拼接
const confirmImport = async () => {
  if (selected.value.length === 0) return
  exporting.value = true
  try {
    const res = await exportNovelSegments(props.projectId, [...selected.value])
    const text = res?.data?.content ?? ''
    if (!text) {
      throw new Error('拼接结果为空')
    }
    emit('select', {
      name: `已导入 ${res?.data?.exported_segments || selected.value.length} 个段落`,
      text
    })
    emit('update:modelValue', false)
  } catch (e) {
    console.error('[NovelReader] export failed:', e)
    ElMessage.error(e.message || '导入所选段落失败')
  } finally {
    exporting.value = false
  }
}
</script>

<style scoped lang="scss">
.reader-body {
  min-height: 400px;
}

.reader-toolbar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 12px;
  margin-bottom: 10px;
  border-radius: 6px;
  background: #f5f7fa;

  .seg-count,
  .selected-count {
    font-size: 13px;
    color: #606266;
  }

  .selected-count {
    color: #409eff;
    font-weight: 600;
  }

  .toolbar-actions {
    margin-left: auto;
    display: flex;
    gap: 8px;
  }
}

.reader-main {
  display: flex;
  gap: 12px;
  height: 56vh;
  min-height: 380px;
}

.toc-panel {
  width: 300px;
  flex-shrink: 0;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;

  .toc-item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    font-size: 13px;
    border-bottom: 1px solid #f2f6fc;
    transition: background 0.15s;

    &:hover {
      background: #f5f7fa;
    }

    &.is-active {
      background: #ecf5ff;

      .toc-title {
        color: #409eff;
        font-weight: 600;
      }
    }

    &.is-bookmark {
      box-shadow: inset 3px 0 0 #e6a23c;
    }

    .toc-title {
      flex: 1;
      color: #303133;
      cursor: pointer;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .toc-size {
      font-size: 11px;
      color: #909399;
      flex-shrink: 0;
    }
  }
}

.preview-panel {
  flex: 1;
  overflow-y: auto;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 16px;

  .preview-text {
    font-size: 14px;
    line-height: 1.9;
    color: #303133;
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
